import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import axios from 'axios';
import { Bar, Doughnut, Line, Pie, Radar } from 'react-chartjs-2';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  RadialLinearScale,
  Tooltip,
} from 'chart.js';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Button,
  Card,
  CardBody,
  FormGroup,
  Input,
  Label,
  Table,
} from 'reactstrap';
import { AlertCircle, Filter, LineChart, Send, Settings, Sparkles } from 'lucide-react';
import Heading from 'components/Heading';
import { authContext } from 'contexts/AuthContext';
import { STATES_BY_COUNTRY } from 'pages/MarketListing/MarketListingGraphQL';
import { ROLES_LIST } from 'utils/CommonGraphQL';
import { modifiedUnderScore } from 'utils/constants';
import { role as roleConfig } from 'config/role';
import PromptSuggestionsSidebar from './PromptSuggestionsSidebar';
import './AIChatBot.css';
/* eslint-disable react/no-multi-comp */
const Typewriter = ({ text, delay = 15, onComplete }) => {
  const [displayedText, setDisplayedText] = useState('');
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    setDisplayedText('');
    let i = 0;
    if (!text) return;
    
    const interval = setInterval(() => {
      setDisplayedText(text.slice(0, i + 1));
      i += 1;
      if (i >= text.length) {
        clearInterval(interval);
        if (onCompleteRef.current) onCompleteRef.current();
      }
    }, delay);
    
    return () => clearInterval(interval);
  }, [text, delay]); 
  
  return <span>{displayedText}</span>;
};

const StreamingInsights = ({ summary, insights, onComplete }) => {
  const [step, setStep] = useState(0); 
  
  useEffect(() => {
    if (step === insights.length + 1 && onComplete) {
      onComplete();
    } else if (step === 1 && insights.length === 0 && onComplete) {
      onComplete();
    }
  }, [step, insights.length, onComplete]);

  return (
    <div className="ai-insights-text mb-4">
       <p style={{ fontWeight: '500', color: '#0f172a' }}>
         {step >= 0 ? <Typewriter text={summary} onComplete={() => setStep(1)} /> : null}
       </p>
       <ul style={{ marginTop: '12px' }}>
         {insights.map((insight, index) => (
           step >= index + 1 ? (
             <li key={insight} style={{ marginBottom: '8px', color: '#334155' }}>
               <Typewriter text={insight} onComplete={() => setStep(index + 2)} />
             </li>
           ) : null
         ))}
       </ul>
    </div>
  );
};

const DataAnalyzerSteps = () => {
  const [step, setStep] = useState(0);
  const steps = [
    "Fetching live data from database...",
    "Cleaning and standardizing records...",
    "Analyzing patterns and trends...",
    "Structuring data for visualization...",
    "Generating AI insights..."
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setStep((prev) => (prev < steps.length - 1 ? prev + 1 : prev));
    }, 700); 
    return () => clearInterval(interval);
  }, [steps.length]);

  return (
    <div className="data-analyzer-container" style={{ textAlign: 'left', maxWidth: '400px', margin: '40px auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
         <div className="spinner-border text-primary" role="status" style={{ width: '24px', height: '24px' }} />
         <h5 style={{ margin: 0, color: '#0f172a' }}>AI Analyzer is working...</h5>
      </div>
      <div className="analyzer-steps" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {steps.map((text, index) => {
          if (index > step) return null;
          const isCurrent = index === step;
          return (
            <div key={text} style={{ display: 'flex', alignItems: 'center', gap: '12px', color: isCurrent ? '#0f172a' : '#64748b', transition: 'all 0.3s ease', opacity: 1, transform: 'translateY(0)', animation: 'fadeInUp 0.3s ease forwards' }}>
              {isCurrent ? (
                <div className="spinner-border spinner-border-sm text-primary" role="status" style={{ width: '16px', height: '16px', borderWidth: '0.15em' }} />
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#24b84a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
              <span style={{ fontWeight: isCurrent ? '500' : '400', fontSize: '14px' }}>{text}</span>
            </div>
          );
        })}
      </div>
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  RadialLinearScale,
  Tooltip,
);

const OUTPUT_TYPES = {
  table: 'dataTable',
  chart: 'chart',
  insights: 'aiInsights',
};

const defaultSettings = {
  [OUTPUT_TYPES.table]: true,
  [OUTPUT_TYPES.chart]: true,
  [OUTPUT_TYPES.insights]: true,
};

const initialFilters = {
  datePreset: 'last_30_days',
  startDate: '2026-04-06',
  endDate: '2026-05-05',
  region: 'All',
  role: 'All',
};

const INDIA_COUNTRY_ID = '101';
const ALL_FILTER_OPTION = { value: 'All', label: 'All' };
const chartColors = ['#242422', '#0d86ff', '#24b84a', '#f58220', '#7c3aed'];
const AI_REPORT_ENDPOINT = `${process.env.REACT_APP_REST_API_ENDPOINT}ai-report/generate`;

const dateOptions = [
  { label: 'Last 7 Days', value: 'last_7_days' },
  { label: 'Last 30 Days', value: 'last_30_days' },
  { label: 'Last Year', value: 'last_year' },
  { label: 'Custom', value: 'custom' },
];

// eslint-disable-next-line no-unused-vars
const aiReportExamples = [
  {
    id: 'weekly-orders',
    title: 'Weekly Orders Trend',
    keywords: ['bar', 'orders', 'weekly', 'bidding', 'default'],
    summary:
      'Bidding conversion is strongest in West region, while North has the highest open pipeline. Sales roles show better follow-up consistency in the last 30 days.',
    chart: {
      type: 'bar',
      label: 'Orders',
      labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
      values: [320, 410, 365, 452],
    },
    table: [
      {
        region: 'West',
        role: 'Sales Executive',
        orders: 384,
        conversion: '42%',
        revenue: 'Rs. 18.4L',
      },
      {
        region: 'North',
        role: 'BDE',
        orders: 302,
        conversion: '34%',
        revenue: 'Rs. 13.8L',
      },
      {
        region: 'South',
        role: 'Sales Head',
        orders: 276,
        conversion: '39%',
        revenue: 'Rs. 15.1L',
      },
      {
        region: 'East',
        role: 'Account',
        orders: 186,
        conversion: '31%',
        revenue: 'Rs. 9.6L',
      },
    ],
    insights: [
      'West region should get priority for high-value bidding follow-ups.',
      'North region has more open orders, but conversion needs attention.',
      'BDE users can improve impact with a shorter first-response SLA.',
    ],
  },
  {
    id: 'region-share',
    title: 'Region Revenue Share',
    keywords: ['pie', 'region', 'share', 'revenue split'],
    summary:
      'Revenue share is concentrated in West and South regions. East has a smaller share but shows stable conversion quality.',
    chart: {
      type: 'pie',
      label: 'Revenue Share',
      labels: ['West', 'North', 'South', 'East', 'Central'],
      values: [34, 22, 26, 12, 6],
    },
    table: [
      {
        region: 'West',
        role: 'Sales Executive',
        orders: 428,
        conversion: '44%',
        revenue: 'Rs. 21.6L',
      },
      {
        region: 'South',
        role: 'Sales Head',
        orders: 318,
        conversion: '41%',
        revenue: 'Rs. 16.4L',
      },
      {
        region: 'North',
        role: 'BDE',
        orders: 284,
        conversion: '33%',
        revenue: 'Rs. 14.2L',
      },
      {
        region: 'East',
        role: 'Account',
        orders: 172,
        conversion: '35%',
        revenue: 'Rs. 7.8L',
      },
    ],
    insights: [
      'West contributes the largest revenue share and should keep high-value follow-ups active.',
      'South is close behind with better conversion discipline.',
      'East needs more pipeline volume before performance can scale.',
    ],
  },
  {
    id: 'conversion-trend',
    title: 'Conversion Trend',
    keywords: ['line', 'trend', 'conversion', 'growth'],
    summary:
      'Conversion has improved gradually across the last five weeks, with the strongest jump after week 3.',
    chart: {
      type: 'line',
      label: 'Conversion %',
      labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5'],
      values: [28, 31, 33, 39, 43],
    },
    table: [
      {
        region: 'West',
        role: 'Sales Executive',
        orders: 396,
        conversion: '43%',
        revenue: 'Rs. 19.8L',
      },
      {
        region: 'South',
        role: 'Sales Head',
        orders: 288,
        conversion: '40%',
        revenue: 'Rs. 14.9L',
      },
      {
        region: 'North',
        role: 'BDE',
        orders: 265,
        conversion: '36%',
        revenue: 'Rs. 12.7L',
      },
      {
        region: 'East',
        role: 'Account',
        orders: 190,
        conversion: '32%',
        revenue: 'Rs. 8.4L',
      },
    ],
    insights: [
      'Week 4 onward shows better sales consistency.',
      'North conversion is improving but still below West and South.',
      'Keep weekly review focused on response time and pending quotes.',
    ],
  },
  {
    id: 'role-performance',
    title: 'Role Performance Mix',
    keywords: ['doughnut', 'donut', 'role', 'performance'],
    summary:
      'Sales Executive and BDE teams are driving most activity. Account users need a tighter follow-up queue.',
    chart: {
      type: 'doughnut',
      label: 'Role Contribution',
      labels: ['Sales Executive', 'BDE', 'Sales Head', 'Account', 'RM'],
      values: [36, 28, 18, 10, 8],
    },
    table: [
      {
        region: 'West',
        role: 'Sales Executive',
        orders: 415,
        conversion: '45%',
        revenue: 'Rs. 20.2L',
      },
      {
        region: 'North',
        role: 'BDE',
        orders: 336,
        conversion: '35%',
        revenue: 'Rs. 14.7L',
      },
      {
        region: 'South',
        role: 'Sales Head',
        orders: 246,
        conversion: '39%',
        revenue: 'Rs. 13.8L',
      },
      {
        region: 'East',
        role: 'Account',
        orders: 132,
        conversion: '29%',
        revenue: 'Rs. 6.1L',
      },
    ],
    insights: [
      'Sales Executive users own the largest share of successful activity.',
      'BDE has strong volume but needs better conversion support.',
      'Account follow-ups should be reviewed daily for delayed responses.',
    ],
  },
  {
    id: 'vendor-health',
    title: 'Vendor Health Score',
    keywords: ['radar', 'vendor', 'health', 'score'],
    summary:
      'Vendor health is strongest in KYC and billing readiness. Follow-up freshness and repeat orders need attention.',
    chart: {
      type: 'radar',
      label: 'Health Score',
      labels: ['KYC', 'Billing', 'Repeat Orders', 'Follow-up', 'Revenue'],
      values: [88, 76, 58, 64, 72],
    },
    table: [
      {
        region: 'West',
        role: 'RM',
        orders: 348,
        conversion: '38%',
        revenue: 'Rs. 17.9L',
      },
      {
        region: 'South',
        role: 'Sales Executive',
        orders: 292,
        conversion: '37%',
        revenue: 'Rs. 13.2L',
      },
      {
        region: 'North',
        role: 'BDE',
        orders: 254,
        conversion: '32%',
        revenue: 'Rs. 11.9L',
      },
      {
        region: 'East',
        role: 'Account',
        orders: 148,
        conversion: '30%',
        revenue: 'Rs. 7.2L',
      },
    ],
    insights: [
      'KYC completion is healthy and can support faster onboarding.',
      'Repeat orders are weaker than revenue score, so retention needs focus.',
      'Follow-up freshness should be tracked for inactive vendors.',
    ],
  },
];

const getChartBackgroundColor = (
  isCircularChart,
  isLineChart,
  isRadarChart,
) => {
  if (isCircularChart) {
    return chartColors;
  }

  if (isLineChart || isRadarChart) {
    return 'rgba(13, 134, 255, 0.12)';
  }

  return '#242422';
};

const formatTableHeader = (key) =>
  key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const isDateField = (key = '') =>
  /(date|time|created|updated|expiry|expired|timestamp)/i.test(String(key));

const formatDateValue = (value, key) => {
  if (!isDateField(key)) {
    return null;
  }

  const rawValue = String(value).trim();
  const numericValue = Number(rawValue);
  const isEpochValue =
    rawValue.length >= 10 && Number.isFinite(numericValue) && numericValue > 0;
  const dateValue = isEpochValue
    ? new Date(rawValue.length === 10 ? numericValue * 1000 : numericValue)
    : new Date(rawValue);

  if (Number.isNaN(dateValue.getTime())) {
    return null;
  }

  return dateValue.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatCellValue = (value, key) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  const formattedDate = formatDateValue(value, key);
  if (formattedDate) {
    return formattedDate;
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(', ') : '-';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return value;
};

const isNumericValue = (value) =>
  value !== null && value !== '' && !Number.isNaN(Number(value));

const getFieldCounts = (rows, field) =>
  rows.reduce((acc, row) => {
    const rawValue = row?.[field];
    const label = formatCellValue(rawValue, field);
    acc[label] = (acc[label] || 0) + 1;
    return acc;
  }, {});

const getBestCategoryField = (rows, columns) => {
  const preferredFields = [
    'status',
    'type',
    'demand_type',
    'offer_type',
    'role',
    'region',
    'state',
    'city',
    'quantity_unit',
    'rate_unit',
  ];
  const availableColumns = columns.filter((column) =>
    rows.some((row) => row?.[column] !== null && row?.[column] !== undefined),
  );
  const preferredField = preferredFields.find((field) =>
    availableColumns.includes(field),
  );

  if (preferredField) {
    return preferredField;
  }

  return availableColumns.find((column) => {
    const uniqueValues = new Set(
      rows.map((row) => formatCellValue(row?.[column], column)).filter(Boolean),
    );
    return uniqueValues.size > 1 && uniqueValues.size <= 8;
  });
};

const getBestNumericField = (rows, columns) => {
  const preferredFields = [
    'count',
    'total',
    'amount',
    'total_qty',
    'minimum_qty',
    'minimum_lifting_qty',
    'rate',
    'view_count',
    'orders',
    'revenue',
  ];
  const availableColumns = columns.filter((column) =>
    rows.some((row) => isNumericValue(row?.[column])),
  );
  const preferredField =
    availableColumns.find((col) =>
      preferredFields.some((pref) => col.toLowerCase().includes(pref)),
    ) || availableColumns.find((column) => !/id$/i.test(column));

  return preferredField;
};

const buildChartFromReportData = (rows, columns) => {
  if (!rows.length || !columns.length) {
    return {
      type: 'bar',
      label: 'Records',
      labels: [],
      values: [],
    };
  }

  const isRawList = columns.some((c) => /^id$/i.test(c));

  if (isRawList) {
    const dateField =
      columns.find((c) => /created_at|createdAt/i.test(c)) ||
      columns.find((c) => isDateField(c));
    const categoryField = dateField || getBestCategoryField(rows, columns);

    if (categoryField) {
      const counts = getFieldCounts(rows, categoryField);
      let entries = Object.entries(counts);

      if (isDateField(categoryField)) {
        entries.sort((a, b) => new Date(a[0]) - new Date(b[0]));
      } else {
        entries.sort((a, b) => b[1] - a[1]);
      }

      entries = entries.slice(0, 15);

      return {
        type:
          entries.length <= 5 && !isDateField(categoryField)
            ? 'doughnut'
            : 'bar',
        label: 'Number of Records',
        labels: entries.map(([label]) => label),
        values: entries.map(([, value]) => value),
      };
    }
  }

  const numericField = getBestNumericField(rows, columns);
  const categoryField = getBestCategoryField(rows, columns);

  if (numericField) {
    const labelField =
      categoryField && categoryField !== numericField
        ? categoryField
        : columns.find((c) => c !== numericField && !/id$/i.test(c)) ||
          columns.find((c) => c !== numericField) ||
          columns[0];

    const entries = rows
      .slice(0, 15)
      .map((row, index) => [
        formatCellValue(row?.[labelField], labelField) ||
          (row?.id ? `#${row.id}` : `Record ${index + 1}`),
        Number(row?.[numericField]) || 0,
      ]);

    return {
      type: 'bar',
      label: formatTableHeader(numericField),
      labels: entries.map(([label]) => label),
      values: entries.map(([, value]) => value),
    };
  }

  if (categoryField) {
    const counts = getFieldCounts(rows, categoryField);
    const entries = Object.entries(counts).slice(0, 15);

    return {
      type: entries.length <= 5 ? 'doughnut' : 'bar',
      label: `${formatTableHeader(categoryField)} Count`,
      labels: entries.map(([label]) => label),
      values: entries.map(([, value]) => value),
    };
  }

  return {
    type: 'bar',
    label: 'Records',
    labels: rows.slice(0, 15).map((_, index) => `Record ${index + 1}`),
    values: rows.slice(0, 15).map(() => 1),
  };
};

const buildInsightsFromReportData = (apiData, rows, columns) => {
  const insights = [];
  const totalRecords = apiData?.totalRecords ?? rows.length;
  const categoryField = getBestCategoryField(rows, columns);
  const numericField = getBestNumericField(rows, columns);

  insights.push(
    `${totalRecords} record${totalRecords === 1 ? '' : 's'} returned for this prompt.`,
  );

  if (categoryField) {
    const [topLabel, topCount] =
      Object.entries(getFieldCounts(rows, categoryField)).sort(
        (a, b) => b[1] - a[1],
      )[0] || [];

    if (topLabel) {
      insights.push(
        `${formatTableHeader(categoryField)} is concentrated around ${topLabel} (${topCount} record${topCount === 1 ? '' : 's'}).`,
      );
    }
  }

  if (numericField) {
    const total = rows.reduce(
      (sum, row) => sum + (Number(row?.[numericField]) || 0),
      0,
    );

    insights.push(
      `Total ${formatTableHeader(numericField).toLowerCase()} across returned rows is ${total}.`,
    );
  }

  if (apiData?.generatedSql) {
    insights.push(
      'Query was generated successfully by AI and applied to live data.',
    );
  }

  return insights;
};

const getReportTables = (apiData, rows, columns) => {
  if (Array.isArray(apiData?.tables) && apiData.tables.length) {
    return apiData.tables.map((tableItem, index) => ({
      title: tableItem?.title || `Report table ${index + 1}`,
      columns: Array.isArray(tableItem?.columns)
        ? tableItem.columns
        : columns,
      rows: Array.isArray(tableItem?.rows) ? tableItem.rows : [],
      totalRecords: tableItem?.totalRecords,
    }));
  }

  return rows.length || columns.length
    ? [{ title: 'Report data', columns, rows }]
    : [];
};

const getReportTitleFromPrompt = (value) => {
  const cleanValue = value.trim();

  if (!cleanValue) {
    return 'AI Generated Report';
  }

  return cleanValue
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const normalizeApiReport = (apiData, promptValue) => {
  const apiTables = Array.isArray(apiData?.tables) ? apiData.tables : [];
  const firstApiTable = apiTables[0] || {};
  const rows = Array.isArray(apiData?.tableData)
    ? apiData.tableData
    : Array.isArray(firstApiTable.rows)
      ? firstApiTable.rows
      : [];
  const responseColumns = Array.isArray(apiData?.columns)
    ? apiData.columns
    : Array.isArray(firstApiTable.columns)
      ? firstApiTable.columns
      : [];
  const fallbackColumns = Array.from(
    rows.reduce((keys, row) => {
      Object.keys(row || {}).forEach((key) => keys.add(key));
      return keys;
    }, new Set()),
  );
  const columns = responseColumns.length ? responseColumns : fallbackColumns;
  const tables = getReportTables(apiData, rows, columns);

  return {
    title:
      apiData?.title || apiData?.reportTitle || getReportTitleFromPrompt(promptValue),
    summary:
      apiData?.executiveSummary ||
      apiData?.summary ||
      `Found ${apiData?.totalRecords ?? rows.length} record(s) for this prompt.`,
    chart: buildChartFromReportData(rows, columns),
    table: rows,
    columns,
    tables,
    insights: Array.isArray(apiData?.insights) && apiData.insights.length
      ? apiData.insights
      : buildInsightsFromReportData(apiData, rows, columns),
    recommendations: apiData?.recommendations || [],
    risks: apiData?.risks || [],
    opportunities: apiData?.opportunities || [],
    kpis: apiData?.kpis || [],
    businessMetrics: apiData?.businessMetrics || [],
    warning: apiData?.warning || '',
    generatedSql: apiData?.generatedSql || '',
  };
};

const formatMetricValue = (value, unit) => {
  if (value === null || value === undefined || value === '') return '-';
  const numericValue = Number(value);
  const formattedValue = Number.isFinite(numericValue)
    ? numericValue.toLocaleString('en-IN', { maximumFractionDigits: 2 })
    : String(value);
  return unit ? `${formattedValue} ${unit}` : formattedValue;
};

const ReportNarrative = ({ result }) => {
  const sections = [
    { key: 'recommendations', title: 'Recommendations', tone: 'positive' },
    { key: 'risks', title: 'Risks to watch', tone: 'warning' },
    { key: 'opportunities', title: 'Opportunities', tone: 'info' },
  ];

  return (
    <>
      {result?.warning && (
        <div className="ai-report-warning">{result.warning}</div>
      )}
      {result?.kpis?.length > 0 && (
        <div className="ai-kpi-grid">
          {result.kpis.map((kpi) => (
            <div className="ai-kpi-card" key={kpi.label}>
              <span>{kpi.label}</span>
              <strong>{formatMetricValue(kpi.value, kpi.unit)}</strong>
            </div>
          ))}
        </div>
      )}
      {result?.businessMetrics?.length > 0 && (
        <Card className="ai-output-card ai-metrics-card">
          <CardBody>
            <div className="ai-report-heading">
              <h5>Business metrics</h5>
              <span>{result.businessMetrics.length} metrics</span>
            </div>
            <div className="ai-metrics-list">
              {result.businessMetrics.map((metric) => (
                <div className="ai-metric-row" key={`${metric.name}-${metric.source || ''}`}>
                  <span>{metric.name}</span>
                  <strong>{formatMetricValue(metric.value, metric.unit)}</strong>
                  {metric.source && <small>{metric.source}</small>}
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}
      {sections.map((section) =>
        result?.[section.key]?.length > 0 ? (
          <Card className={`ai-output-card ai-narrative-card ${section.tone}`} key={section.key}>
            <CardBody>
              <div className="ai-report-heading"><h5>{section.title}</h5></div>
              <ul>
                {result[section.key].map((item) => <li key={item}>{item}</li>)}
              </ul>
            </CardBody>
          </Card>
        ) : null,
      )}
    </>
  );
};

const AdditionalReportTables = ({ tables }) =>
  tables?.slice(1).map((reportTable) => (
      <Card className="ai-output-card ai-table-card" key={reportTable.title}>
        <CardBody>
          <div className="ai-report-heading">
            <h5>{reportTable.title}</h5>
            <span>{reportTable.totalRecords || reportTable.rows.length} records</span>
          </div>
          {reportTable.columns?.length ? (
            <div className="table-border">
              <Table responsive className="common-table mb-0">
                <thead><tr>{reportTable.columns.map((column) => <th key={column}>{formatTableHeader(column)}</th>)}</tr></thead>
                <tbody>
                  {reportTable.rows.map((row) => (
                    <tr key={row.id || `${reportTable.title}-${Object.values(row).join('-')}`}>
                      {reportTable.columns.map((column) => <td key={column}>{formatCellValue(row?.[column], column)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : <div className="ai-no-records">No records found.</div>}
        </CardBody>
      </Card>
    ));

const getReportFromJsonPrompt = (value) => {
  try {
    const parsedReport = JSON.parse(value);
    const chartType = parsedReport?.chart?.type;

    if (
      !chartType ||
      !['bar', 'line', 'pie', 'doughnut', 'radar'].includes(chartType)
    ) {
      return null;
    }

    return {
      title: parsedReport.title || 'AI Generated Report',
      summary: parsedReport.summary || '',
      chart: {
        label: parsedReport.chart.label || 'Value',
        labels: parsedReport.chart.labels || [],
        type: chartType,
        values: parsedReport.chart.values || [],
      },
      table: parsedReport.table || [],
      insights: parsedReport.insights || [],
    };
  } catch {
    return null;
  }
};

const AIChatBot = () => {
  const { auth } = useContext(authContext);
  const [prompt, setPrompt] = useState('');
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [settings, setSettings] = useState(defaultSettings);
  const [filters, setFilters] = useState(initialFilters);
  const [result, setResult] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [reportError, setReportError] = useState('');
  const [activeChartTab, setActiveChartTab] = useState('bar');
  const [revealedSections, setRevealedSections] = useState({ table: false, chart: false, insights: false });
  const [isInsightsTypingDone, setIsInsightsTypingDone] = useState(false);
  const promptInputRef = useRef(null);
  const settingsMenuRef = useRef(null);
  const filterMenuRef = useRef(null);

  const handleSelectPrompt = (promptText) => {
    // Only autofill the actual prompt, not the title
    setPrompt(promptText);
    setSuggestionsOpen(false);
    if (promptInputRef.current) {
      promptInputRef.current.focus();
    }
  };
  const {
    data: statesData,
    loading: statesLoading,
    error: statesError,
  } = useQuery(STATES_BY_COUNTRY, {
    variables: { country: INDIA_COUNTRY_ID },
    fetchPolicy: 'network-only',
  });
  const {
    data: rolesData,
    loading: rolesLoading,
    error: rolesError,
  } = useQuery(ROLES_LIST, {
    variables: {
      filter: [],
      pageNo: 0,
      pageSize: 30,
      sortCol: null,
      sortDirection: null,
    },
    fetchPolicy: 'network-only',
  });

  const regionOptions = useMemo(() => {
    const states = statesData?.statesByCountry || [];
    const mappedStates = states.map((state) => ({
      value: String(state.id),
      label: state.stateName,
    }));

    return [ALL_FILTER_OPTION, ...mappedStates];
  }, [statesData]);

  const roleOptions = useMemo(() => {
    const filteredRoles = (rolesData?.roles || []).filter(
      (roleItem) =>
        roleItem?.name !== roleConfig.SUPER_ADMIN &&
        roleItem?.name !== roleConfig.TRADER,
    );
    const mappedRoles = filteredRoles.map((roleItem) => ({
      value: String(roleItem.id),
      label: modifiedUnderScore(roleItem.name),
    }));

    return [ALL_FILTER_OPTION, ...mappedRoles];
  }, [rolesData]);

  const columns = useMemo(() => {
    const tableKeys = result?.columns?.length
      ? result.columns
      : Object.keys(result?.table?.[0] || {});

    if (!tableKeys.length) {
      return [];
    }

    return tableKeys.map((key) => ({
      accessorKey: key,
      header: formatTableHeader(key),
      cell: ({ getValue }) => formatCellValue(getValue(), key),
    }));
  }, [result]);

  const table = useReactTable({
    data: result?.table || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    if (result && !isGenerating) {
      setIsInsightsTypingDone(!settings.aiInsights);
      setRevealedSections({ table: false, chart: false, insights: false });
    }
  }, [result, isGenerating, settings.aiInsights]);

  useEffect(() => {
    if (result?.chart?.type) {
      setActiveChartTab('bar');
    }
    
    // Cascade reveal when typing is done
    if (result && !isGenerating && isInsightsTypingDone) {
      let time = 0;
      if (settings.aiInsights) {
        setTimeout(() => setRevealedSections(prev => ({ ...prev, insights: true })), time);
        time += 400; // Delay before table
      } else {
         setRevealedSections(prev => ({ ...prev, insights: true }));
      }

      if (settings.dataTable) {
        setTimeout(() => setRevealedSections(prev => ({ ...prev, table: true })), time);
        time += 400; // Delay before chart
      } else {
        setRevealedSections(prev => ({ ...prev, table: true }));
      }
      
      if (settings.chart) {
        setTimeout(() => setRevealedSections(prev => ({ ...prev, chart: true })), time);
      } else {
         setRevealedSections(prev => ({ ...prev, chart: true }));
      }
    }
  }, [result, isGenerating, isInsightsTypingDone, settings]);

  const chartData = useMemo(() => {
    if (!result) {
      return { labels: [], datasets: [] };
    }

    if (!result?.chart) {
      return { labels: [], datasets: [] };
    }

    const isCircularChart = ['pie', 'doughnut'].includes(activeChartTab);
    const isLineChart = activeChartTab === 'line';
    const isRadarChart = activeChartTab === 'radar';
    const chartBackgroundColor = getChartBackgroundColor(
      isCircularChart,
      isLineChart,
      isRadarChart,
    );

    return {
      labels: result.chart.labels,
      datasets: [
        {
          label: result.chart.label,
          data: result.chart.values,
          backgroundColor: chartBackgroundColor,
          borderColor: isLineChart || isRadarChart ? '#0d86ff' : '#ffffff',
          pointBackgroundColor: '#0d86ff',
          fill: isRadarChart,
          hoverBackgroundColor: isCircularChart ? chartColors : '#343431',
          borderRadius: 6,
          maxBarThickness: 44,
        },
      ],
    };
  }, [result, activeChartTab]);

  const chartOptions = useMemo(() => {
    if (!result?.chart) {
      return {};
    }

    const showLegend = ['pie', 'doughnut', 'radar'].includes(activeChartTab);
    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: showLegend,
          position: 'bottom',
          labels: { boxWidth: 12, color: '#344054' },
        },
      },
    };

    if (['pie', 'doughnut'].includes(activeChartTab)) {
      return baseOptions;
    }

    if (activeChartTab === 'radar') {
      return {
        ...baseOptions,
        scales: {
          r: {
            beginAtZero: true,
            max: 100,
            grid: { color: '#edf2f2' },
            angleLines: { color: '#edf2f2' },
            pointLabels: { color: '#667085' },
            ticks: { color: '#667085', backdropColor: 'transparent' },
          },
        },
      };
    }

    return {
      ...baseOptions,
      plugins: {
        ...baseOptions.plugins,
        legend: { display: false },
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: { color: '#edf2f2' },
          ticks: { color: '#667085' },
        },
        x: {
          grid: { display: false },
          ticks: { color: '#667085' },
        },
      },
    };
  }, [result, activeChartTab]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        settingsMenuRef.current &&
        !settingsMenuRef.current.contains(event.target)
      ) {
        setSettingsOpen(false);
      }

      if (
        filterMenuRef.current &&
        !filterMenuRef.current.contains(event.target)
      ) {
        setFilterOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const handleSettingChange = (key) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleGenerate = async () => {
    const trimmedPrompt = prompt.trim();
    const parsedJsonReport = getReportFromJsonPrompt(trimmedPrompt);

    setReportError('');
    setSettingsOpen(false);
    setFilterOpen(false);
    setIsInsightsTypingDone(false);
    setRevealedSections({ table: false, chart: false, insights: false });

    if (parsedJsonReport) {
      setResult(parsedJsonReport);
      return;
    }

    if (!trimmedPrompt) {
      setResult(null);
    }

    if (!auth?.data?.token) {
      setResult(null);
      setReportError('Authorization token not available. Please login again.');
      return;
    }

    try {
      setIsGenerating(true);
      const response = await axios.post(
        AI_REPORT_ENDPOINT,
        { prompt: trimmedPrompt },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth.data.token}`,
          },
        },
      );

      if (response?.data?.error) {
        throw new Error(response.data.message || 'Unable to generate report.');
      }

      setResult(normalizeApiReport(response?.data?.data || {}, trimmedPrompt));
    } catch (error) {
      setResult(null);
      setReportError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          'Unable to generate report.',
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const handleExportCSV = () => {
    if (!result?.table?.length) return;

    const keys = result.columns?.length
      ? result.columns
      : Object.keys(result.table[0] || {});

    const csvRows = [];
    csvRows.push(keys.map(formatTableHeader).join(','));

    result.table.forEach((row) => {
      const values = keys.map((key) => {
        const val = formatCellValue(row[key], key);
        let stringVal = String(val ?? '');

        if (isDateField(key) && stringVal !== '-' && stringVal !== '') {
          stringVal = `="${stringVal}"`;
        }

        if (
          stringVal.includes(',') ||
          stringVal.includes('"') ||
          stringVal.includes('\n')
        ) {
          return `"${stringVal.replace(/"/g, '""')}"`;
        }
        return stringVal;
      });
      csvRows.push(values.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `${(result.title || 'export').replace(/\s+/g, '_')}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setSettingsOpen(false);
  };

  const renderChart = () => {
    if (!result?.chart) {
      return null;
    }

    if (activeChartTab === 'line') {
      return <Line data={chartData} options={chartOptions} />;
    }

    if (activeChartTab === 'pie') {
      return <Pie data={chartData} options={chartOptions} />;
    }

    if (activeChartTab === 'doughnut') {
      return <Doughnut data={chartData} options={chartOptions} />;
    }

    if (activeChartTab === 'radar') {
      return <Radar data={chartData} options={chartOptions} />;
    }

    return <Bar data={chartData} options={chartOptions} />;
  };

  return (
    <div className="ai-chatbot-page">
      <div className="pb-1 mb-3 top-head">
        <Heading title="AI Report" />
      </div>

      <div className="ai-chatbot-shell">
        <div className={`ai-chatbot-layout-wrapper ${suggestionsOpen ? 'with-sidebar' : ''}`}>
          <div className="ai-chatbot-main-column">
            <div className={`ai-chatbot-search ${suggestionsOpen ? 'compact' : ''}`}>
              <div className="ai-chatbot-input-wrap">
                <Input
                  innerRef={promptInputRef}
                  type="textarea"
                  value={prompt}
                  onChange={(event) => setPrompt(event.target.value)}
                  placeholder="Ask for analytics, orders, vendors, bids..."
                  className="ai-chatbot-input"
                />
              </div>

              <div className="ai-chatbot-actions">
                <Button
                  type="button"
                  color="link"
                  className={`ai-icon-btn ${suggestionsOpen ? 'active' : ''}`}
                  onClick={() => {
                    setSuggestionsOpen((prev) => !prev);
                    setSettingsOpen(false);
                    setFilterOpen(false);
                  }}
                  title={suggestionsOpen ? 'Close suggestions' : 'Prompt suggestions'}
                >
                  <Sparkles size={18} />
                </Button>

                <div className="ai-action-menu-wrap" ref={settingsMenuRef}>
              <Button
                type="button"
                color="link"
                className="ai-icon-btn"
                onClick={() => {
                  setSettingsOpen((prev) => !prev);
                  setFilterOpen(false);
                }}
                title="Settings"
              >
                <Settings size={18} />
              </Button>
              {settingsOpen && (
                <div className="ai-popover">
                  <h6>Settings</h6>
                  <FormGroup check>
                    <Input
                      id="ai-data-table"
                      type="checkbox"
                      checked={settings.dataTable}
                      onChange={() => handleSettingChange(OUTPUT_TYPES.table)}
                    />
                    <Label check for="ai-data-table">
                      Data Table
                    </Label>
                  </FormGroup>
                  <FormGroup check>
                    <Input
                      id="ai-chart"
                      type="checkbox"
                      checked={settings.chart}
                      onChange={() => handleSettingChange(OUTPUT_TYPES.chart)}
                    />
                    <Label check for="ai-chart">
                      Chart
                    </Label>
                  </FormGroup>
                  <FormGroup check>
                    <Input
                      id="ai-insights"
                      type="checkbox"
                      checked={settings.aiInsights}
                      onChange={() =>
                        handleSettingChange(OUTPUT_TYPES.insights)
                      }
                    />
                    <Label check for="ai-insights">
                      AI Insights
                    </Label>
                  </FormGroup>

                  {result?.table?.length > 0 && (
                    <div
                      className="mt-3 pt-3"
                      style={{ borderTop: '1px solid #e2e8f0' }}
                    >
                      <Button
                        type="button"
                        color="primary"
                        size="sm"
                        className="w-100 ai-export-btn"
                        onClick={handleExportCSV}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        Export CSV
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="ai-action-menu-wrap" ref={filterMenuRef}>
              <Button
                type="button"
                color="link"
                className="ai-icon-btn"
                onClick={() => {
                  setFilterOpen((prev) => !prev);
                  setSettingsOpen(false);
                }}
                title="Filter"
              >
                <Filter size={18} />
              </Button>
              {filterOpen && (
                <div className="ai-popover ai-filter-popover">
                  <h6>Filters</h6>
                  <div className="ai-filter-group">
                    <Label>Date Filter</Label>
                    <div className="ai-date-options">
                      {dateOptions.map((option) => (
                        <Button
                          key={option.value}
                          type="button"
                          color="link"
                          className={
                            filters.datePreset === option.value
                              ? 'ai-date-option active'
                              : 'ai-date-option'
                          }
                          onClick={() =>
                            handleFilterChange('datePreset', option.value)
                          }
                        >
                          {option.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {filters.datePreset === 'custom' && (
                    <div className="ai-custom-date-row">
                      <FormGroup>
                        <Label>From</Label>
                        <Input
                          type="date"
                          value={filters.startDate}
                          onChange={(event) =>
                            handleFilterChange('startDate', event.target.value)
                          }
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>To</Label>
                        <Input
                          type="date"
                          value={filters.endDate}
                          onChange={(event) =>
                            handleFilterChange('endDate', event.target.value)
                          }
                        />
                      </FormGroup>
                    </div>
                  )}

                  <FormGroup>
                    <Label>Region</Label>
                    <Input
                      type="select"
                      value={filters.region}
                      disabled={statesLoading}
                      onChange={(event) =>
                        handleFilterChange('region', event.target.value)
                      }
                    >
                      {statesLoading && (
                        <option value={filters.region}>
                          Loading states...
                        </option>
                      )}
                      {!statesLoading &&
                        regionOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                    </Input>
                    {statesError && (
                      <div className="ai-filter-error">
                        Failed to load states.
                      </div>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <Label>Role</Label>
                    <Input
                      type="select"
                      value={filters.role}
                      disabled={rolesLoading}
                      onChange={(event) =>
                        handleFilterChange('role', event.target.value)
                      }
                    >
                      {rolesLoading && (
                        <option value={filters.role}>Loading roles...</option>
                      )}
                      {!rolesLoading &&
                        roleOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                    </Input>
                    {rolesError && (
                      <div className="ai-filter-error">
                        Failed to load roles.
                      </div>
                    )}
                  </FormGroup>
                </div>
              )}
            </div>

            <Button
              type="button"
              color="primary"
              className="ai-generate-btn"
              onClick={handleGenerate}
              disabled={isGenerating}
            >
              <Send size={16} />
              <span>{isGenerating ? 'Generating...' : 'Generate'}</span>
            </Button>
          </div>
        </div>

        {isGenerating && (
          <div className="ai-empty-state">
            <DataAnalyzerSteps />
          </div>
        )}

        {!result && !isGenerating && (
          <div
            className={
              reportError
                ? 'ai-empty-state ai-empty-state-error-only'
                : 'ai-empty-state'
            }
          >
            {reportError ? (
              <div className="ai-report-error-center" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b' }}>
                  <AlertCircle size={18} />
                  <span>Failed to generate report.</span>
                </div>
                <Button color="primary" onClick={handleGenerate} size="sm" style={{ padding: '6px 16px' }}>
                  Retry
                </Button>
              </div>
            ) : (
              <>
                <LineChart size={32} />
                <h5>Generate analytics to view report</h5>
                <p>
                  Chart, insights and table will appear here after you click
                  Generate.
                </p>
              </>
            )}
          </div>
        )}

        {result && !isGenerating && (
          <>
            {settings.aiInsights && (
              <StreamingInsights 
                summary={result.summary} 
                insights={result.insights} 
                onComplete={() => setIsInsightsTypingDone(true)}
              />
            )}

            {settings.aiInsights && revealedSections.insights && (
              <ReportNarrative result={result} />
            )}

            {settings.dataTable && revealedSections.table && (
              <Card className="ai-output-card ai-table-card ai-first-output" style={{ animation: 'fadeInUp 0.5s ease forwards', marginTop: '24px' }}>
                <CardBody>
                  <div className="ai-report-heading">
                    <h5>{result.tables?.[0]?.title || 'Report data'}</h5>
                    <span>{result.tables?.[0]?.totalRecords || result.table.length} records</span>
                  </div>
                  {columns.length ? (
                    <div className="table-border">
                      <Table responsive className="common-table mb-0">
                        <thead>
                          {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                              {headerGroup.headers.map((header) => (
                                <th key={header.id}>
                                  {flexRender(
                                    header.column.columnDef.header,
                                    header.getContext(),
                                  )}
                                </th>
                              ))}
                            </tr>
                          ))}
                        </thead>
                        <tbody>
                          {table.getRowModel().rows.length ? (
                            table.getRowModel().rows.map((row) => (
                              <tr key={row.id}>
                                {row.getVisibleCells().map((cell) => (
                                  <td key={cell.id}>
                                    {flexRender(
                                      cell.column.columnDef.cell,
                                      cell.getContext(),
                                    )}
                                  </td>
                                ))}
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan={columns.length}
                                className="text-center"
                              >
                                No records found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <div className="ai-no-records">No records found.</div>
                  )}
                </CardBody>
              </Card>
            )}

            {settings.dataTable && revealedSections.table && (
              <AdditionalReportTables tables={result.tables} />
            )}

            {settings.chart && revealedSections.chart && (
              <Card className="ai-output-card" style={{ animation: 'fadeInUp 0.5s ease forwards' }}>
                <CardBody>
                  <div className="ai-report-heading" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h5>{result.title}</h5>
                    <div className="d-flex align-items-center" style={{ background: '#f1f5f9', padding: '4px', borderRadius: '8px', gap: '4px' }}>
                      <button
                        type="button"
                        onClick={() => setActiveChartTab('bar')}
                        style={{
                          border: 'none',
                          background: activeChartTab === 'bar' ? '#fff' : 'transparent',
                          boxShadow: activeChartTab === 'bar' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                          padding: '4px 12px',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: activeChartTab === 'bar' ? '600' : '400',
                          color: activeChartTab === 'bar' ? '#0f172a' : '#64748b',
                          transition: 'all 0.2s'
                        }}
                      >
                        Bar Chart
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveChartTab(result.chart.type === 'bar' ? 'line' : result.chart.type)}
                        style={{
                          border: 'none',
                          background: activeChartTab === 'bar' ? 'transparent' : '#fff',
                          boxShadow: activeChartTab === 'bar' ? 'none' : '0 1px 3px rgba(0,0,0,0.1)',
                          padding: '4px 12px',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: activeChartTab === 'bar' ? '400' : '600',
                          color: activeChartTab === 'bar' ? '#64748b' : '#0f172a',
                          textTransform: 'capitalize',
                          transition: 'all 0.2s'
                        }}
                      >
                        {result.chart.type === 'bar' ? 'Line' : result.chart.type} Chart
                      </button>
                    </div>
                  </div>
                  <div className="ai-chart-wrap">{renderChart()}</div>
                </CardBody>
              </Card>
            )}
          </>
        )}
          </div>

          {suggestionsOpen && (
            <PromptSuggestionsSidebar
              currentPrompt={prompt}
              onSelectPrompt={handleSelectPrompt}
              onClose={() => setSuggestionsOpen(false)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AIChatBot;
